"use client";

import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";

import { RecipeTileVisual } from "@/components/shared/RecipeTile";
import { Link } from "@/i18n/navigation";
import { fadeSlideUpVariants, springConfig } from "@/lib/animations";
import type { DashboardEntry } from "@/lib/dashboard/server";
import { cn } from "@/lib/utils";

interface TodayCookingProps {
  entries: DashboardEntry[];
}

const HERO_TITLE_CLASS = "text-2xl font-semibold sm:text-3xl";
const FULL_BLEED_CLASS = "rounded-none sm:rounded-none @md:rounded-xl";

export function TodayCooking({ entries }: TodayCookingProps) {
  const t = useTranslations("dashboard");

  if (entries.length === 0) return null;

  const hasMultiple = entries.length > 1;

  return (
    <motion.section
      variants={fadeSlideUpVariants}
      initial="hidden"
      animate="visible"
      aria-label={t("todayCooking")}
    >
      <div className="mb-3 flex items-baseline justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t("todayCooking")}
        </p>
        {hasMultiple && (
          <p className="text-sm text-muted-foreground">
            {t("todayCookingMultiple", { count: entries.length })}
          </p>
        )}
      </div>

      {hasMultiple ? <TodayCarousel entries={entries} /> : <HeroCard entry={entries[0]} />}
    </motion.section>
  );
}

function HeroCard({ entry }: { entry: DashboardEntry }) {
  return (
    <div className="-mx-4 @md:mx-0">
      <Link href={`/recipes/${entry.recipe.id}`} className="block" prefetch={false}>
        <RecipeTileVisual
          recipe={entry.recipe}
          aspectRatio={21 / 9}
          sizes="(max-width: 768px) 100vw, 800px"
          priority
          hoverScale
          titleClassName={HERO_TITLE_CLASS}
          className={FULL_BLEED_CLASS}
        />
      </Link>
    </div>
  );
}

function TodayCarousel({ entries }: { entries: DashboardEntry[] }) {
  const t = useTranslations("dashboard");
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    skipSnaps: false,
    watchDrag: (_api, evt) => evt instanceof TouchEvent,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div>
      <div className="relative -mx-4 @md:mx-0">
        <div className="overflow-hidden @md:rounded-xl" ref={emblaRef}>
          <div className="flex">
            {entries.map((entry) => (
              <div key={entry.id} className="min-w-0 flex-[0_0_100%]">
                <Link href={`/recipes/${entry.recipe.id}`} className="block" prefetch={false}>
                  <RecipeTileVisual
                    recipe={entry.recipe}
                    aspectRatio={21 / 9}
                    sizes="(max-width: 768px) 100vw, 800px"
                    priority
                    hoverScale
                    titleClassName={HERO_TITLE_CLASS}
                  />
                </Link>
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={scrollPrev}
          disabled={!canScrollPrev}
          className={cn(
            "absolute start-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-opacity",
            "size-9 md:size-12 lg:size-15",
            canScrollPrev ? "opacity-100 hover:bg-black/70" : "opacity-0"
          )}
          aria-label={t("previous")}
        >
          <ChevronLeft className="me-1 size-5 md:size-7 lg:size-9" />
        </button>

        <button
          type="button"
          onClick={scrollNext}
          disabled={!canScrollNext}
          className={cn(
            "absolute end-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-opacity",
            "size-9 md:size-12 lg:size-15",
            canScrollNext ? "opacity-100 hover:bg-black/70" : "opacity-0"
          )}
          aria-label={t("next")}
        >
          <ChevronRight className="ms-1 size-5 md:size-7 lg:size-9" />
        </button>
      </div>

      <div className="mt-3 flex items-center justify-center gap-1.5" role="tablist">
        {entries.map((entry, i) => (
          <motion.button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={i === selectedIndex}
            aria-label={entry.recipe.title}
            onClick={() => emblaApi?.scrollTo(i)}
            className={cn(
              "size-2 rounded-full transition-colors",
              i === selectedIndex ? "bg-primary" : "bg-muted-foreground/30"
            )}
            animate={{ scale: i === selectedIndex ? 1.3 : 1 }}
            transition={springConfig}
          />
        ))}
      </div>
    </div>
  );
}
